// Install entry point — features.md A17, architecture D1/§3.1: Android/desktop get the browser's
// install prompt, iOS Safari gets the Add-to-Home-Screen guide (push needs it there).
import { Button, Card, X } from '@roots/ui';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { onInstallAvailabilityChange, platform } from '../platform/web.js';

const DISMISS_KEY = 'roots:install-dismissed';

export function useInstallState() {
  const canInstall = useSyncExternalStore(onInstallAvailabilityChange, () => platform.canInstall());
  const [standalone, setStandalone] = useState(() => platform.isStandalone());
  useEffect(() => {
    const query = window.matchMedia('(display-mode: standalone)');
    const update = () => setStandalone(platform.isStandalone());
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const iosGuide = !standalone && platform.isIosSafari();
  return { canInstall: canInstall && !standalone, iosGuide };
}

/**
 * `dismissible` cards (Home) hide for good once closed; the Profile copy stays so the
 * option is always findable (App Flow §6 settings).
 */
export function InstallCard({ dismissible = false }: { dismissible?: boolean }) {
  const { canInstall, iosGuide } = useInstallState();
  const [dismissed, setDismissed] = useState(() => {
    if (!dismissible) return false;
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (dismissed || (!canInstall && !iosGuide)) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {}
  };

  return (
    <Card className="relative flex flex-col gap-3">
      {dismissible && (
        <button
          type="button"
          aria-label="Not now"
          onClick={dismiss}
          className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center text-[var(--color-text-secondary)]"
        >
          <X size={18} aria-hidden />
        </button>
      )}
      <p className="type-subheading pr-10">Add Roots to your home screen</p>
      {canInstall ? (
        <>
          <p className="type-body">
            Open your family’s magazine in one tap, and get a nudge on drop day.
          </p>
          <Button className="self-start" onClick={() => void platform.promptInstall()}>
            Install Roots
          </Button>
        </>
      ) : (
        <ol className="type-body flex list-decimal flex-col gap-1 pl-5">
          <li>Tap the Share button at the bottom of Safari.</li>
          <li>Scroll and tap “Add to Home Screen”.</li>
          <li>Open Roots from your Home Screen to get drop-day notifications.</li>
        </ol>
      )}
    </Card>
  );
}
